import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion, type Variants } from 'framer-motion';
import {
  ChevronDown, ChevronUp, Check, ArrowRight,
  TrendingUp, Package, Clock, DollarSign, Zap, Mail,
} from 'lucide-react';
import ClearPathLogo from '../components/ClearPathLogo';
import { PRO_PRICE_LABEL } from '../pricing';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: EASE, delay: i * 0.07 },
  }),
};

const FEATURES = [
  { icon: Mail, title: 'The Daily Brief', desc: 'One email each morning with the few things worth doing today, never a dashboard you have to remember to open.' },
  { icon: DollarSign, title: 'Dollars', desc: 'Every move carries a monthly dollar range computed by code from your own numbers. No LLM guessing at your revenue.' },
  { icon: Package, title: 'Product intelligence', desc: 'Every item ranked by revenue, margin, and momentum. Your stars, your dead weight, and what to do about each.' },
  { icon: Zap, title: 'Bundle radar', desc: 'Finds the products your customers already buy together and turns the pattern into a promotion you can run tomorrow.' },
  { icon: Clock, title: 'Timing intelligence', desc: 'Your peak hours, your dead windows, your staffing math, so you push the right thing at the right moment.' },
  { icon: TrendingUp, title: 'Honest outcomes ledger', desc: 'A running tally of what ClearPath has found you, and how much of it has been measured against reality.' },
];

const FAQS = [
  { q: 'What POS systems does ClearPath work with?', a: 'Square syncs automatically, no exports needed. Clover, Toast, Lightspeed and Shopify POS upload as a CSV, with live connections on the way. Any other register that exports a CSV works too, and the analysis is the same.' },
  { q: 'Is this just another dashboard?', a: 'No, that’s the whole point. ClearPath comes to you. The analytics pages exist as evidence to verify a claim, but the product is the morning brief that tells you what to do without you logging in.' },
  { q: 'Where do the dollar estimates come from?', a: 'From your own transactions, computed by deterministic code, never by an AI. The language in the brief is written by a model; every number is math you could check yourself.' },
  { q: 'Do I need to be technical?', a: 'Not at all. ClearPath is built for owners, not analysts. No formulas, no pivot tables, no configuration. You read three sentences and act.' },
  { q: 'Can I try it before paying?', a: 'Yes. Explore the whole platform with sample data, no credit card, no time limit. When you’re ready to connect your own numbers, upgrade.' },
];

export default function FeaturesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // Land on the right section when arriving via /features#pricing (nav link),
  // otherwise start at the top.
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    }
    window.scrollTo(0, 0);
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-[#FAF9F7] overflow-x-hidden">

      {/* ── Navbar ── */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-[#FAF9F7]/85 border-b border-[#E6E2DE]">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center min-h-[44px] rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF9F7]"><ClearPathLogo size="sm" showText /></Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="inline-flex items-center min-h-[44px] text-sm font-sans text-[#6B6670] hover:text-[#2D2A2E] transition-colors">Overview</Link>
            <a href="#features" className="inline-flex items-center min-h-[44px] text-sm font-sans text-[#6B6670] hover:text-[#2D2A2E] transition-colors">Features</a>
            <a href="#pricing" className="inline-flex items-center min-h-[44px] text-sm font-sans text-[#6B6670] hover:text-[#2D2A2E] transition-colors">Pricing</a>
            <a href="#faq" className="inline-flex items-center min-h-[44px] text-sm font-sans text-[#6B6670] hover:text-[#2D2A2E] transition-colors">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/auth')} className="bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] font-sans font-medium px-5 min-h-[44px] text-sm transition-colors shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF9F7]">
              Get started
            </button>
          </div>
        </div>
      </nav>

      {/* ── Page header ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-30%] left-1/2 -translate-x-1/2 w-[720px] h-[520px] rounded-full bg-[radial-gradient(circle,_rgba(74,45,94,0.10),_transparent_65%)]" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 pt-24 pb-10 text-center">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="font-sans text-sm text-[#4A2D5E] font-medium mb-3">
            The details
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE, delay: 0.05 }} className="font-serif text-4xl md:text-5xl text-[#2D2A2E] tracking-[-0.02em] leading-tight">
            &ldquo;What should I do today?&rdquo;
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE, delay: 0.1 }} className="mt-5 font-sans text-[#6B6670] max-w-xl mx-auto leading-relaxed">
            That&rsquo;s the question every owner wakes up with. Everything on this page exists to answer it —
            and to put a dollar figure on your next step.
          </motion.p>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="scroll-mt-20 bg-white border-y border-[#E6E2DE]">
        <div className="max-w-5xl mx-auto px-6 py-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={{ visible: { transition: { staggerChildren: 0.07 } } }}>
            <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-4xl text-center text-[#2D2A2E] mb-3 tracking-tight">
              Not a dashboard. A decision.
            </motion.h2>
            <motion.p variants={fadeUp} className="font-sans text-[#6B6670] text-center max-w-lg mx-auto mb-14 leading-relaxed">
              Everything below feeds one thing: the shortest possible answer to &ldquo;what is my next step?&rdquo;
            </motion.p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {FEATURES.map((f, i) => (
                <motion.div key={i} custom={i} variants={fadeUp} className="group bg-[#FAF9F7] hover:bg-white border border-[#E6E2DE] rounded-[14px] p-6 transition-all duration-300 hover:shadow-[0_10px_30px_-12px_rgba(74,45,94,0.25)] hover:-translate-y-1">
                  <div className="w-10 h-10 rounded-[10px] bg-[#4A2D5E]/8 flex items-center justify-center mb-4 group-hover:bg-[#4A2D5E] transition-colors duration-300">
                    <f.icon size={18} strokeWidth={1.5} className="text-[#4A2D5E] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-serif text-lg text-[#2D2A2E] mb-2">{f.title}</h3>
                  <p className="font-sans text-sm text-[#6B6670] leading-relaxed">{f.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="scroll-mt-20 bg-[#FAF9F7] border-b border-[#E6E2DE]">
        <div className="max-w-xl mx-auto px-6 py-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={{ visible: { transition: { staggerChildren: 0.08 } } }}>
            <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-4xl text-center text-[#2D2A2E] mb-12 tracking-tight">
              One plan. Everything included.
            </motion.h2>
            <motion.div variants={fadeUp} className="bg-white border border-[#E6E2DE] rounded-[16px] overflow-hidden shadow-[0_10px_40px_-16px_rgba(74,45,94,0.2)] max-w-md mx-auto">
              <div className="p-8 text-center">
                <p className="font-sans text-4xl font-semibold text-[#2D2A2E] tabular-nums">
                  {PRO_PRICE_LABEL}<span className="text-base font-normal text-[#6B6670]">/mo</span>
                </p>
                <ul className="mt-7 space-y-2.5 text-left max-w-xs mx-auto">
                  {['Your own data', 'Square sync, CSV for the rest', 'The Daily Brief', 'All 12 analytics views', 'Measured outcomes ledger', 'Priority support'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 font-sans text-sm text-[#6B6670]">
                      <Check size={15} strokeWidth={2} className="text-[#2E6B4F] mt-0.5 flex-shrink-0" />{item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="px-8 pb-8">
                <button onClick={() => navigate('/auth')} className="w-full bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] font-sans font-medium px-6 py-3.5 transition-colors shadow-sm text-sm">
                  Get started
                </button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="scroll-mt-20 max-w-2xl mx-auto px-6 py-24">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={{ visible: { transition: { staggerChildren: 0.06 } } }}>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-4xl text-center text-[#2D2A2E] mb-10 tracking-tight">
            Questions
          </motion.h2>
          <div className="space-y-2">
            {FAQS.map((faq, i) => (
              <motion.div key={i} variants={fadeUp} custom={i} className="bg-white border border-[#E6E2DE] rounded-[12px] overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-answer-${i}`}
                  className="w-full flex items-center justify-between p-5 text-left group gap-4"
                >
                  <span className="font-serif text-base text-[#2D2A2E] group-hover:text-[#4A2D5E] transition-colors">{faq.q}</span>
                  {openFaq === i
                    ? <ChevronUp size={17} strokeWidth={1.5} className="text-[#6B6670] flex-shrink-0" aria-hidden="true" />
                    : <ChevronDown size={17} strokeWidth={1.5} className="text-[#6B6670] flex-shrink-0" aria-hidden="true" />}
                </button>
                {openFaq === i && (
                  <div id={`faq-answer-${i}`} className="px-5 pb-5">
                    <p className="font-sans text-sm text-[#6B6670] leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── Final CTA ── */}
      <section className="bg-[#4A2D5E] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_70%_30%,_white,_transparent_50%)]" />
        <div className="relative max-w-2xl mx-auto px-6 py-24 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
            <motion.h2 variants={fadeUp} className="font-serif text-4xl md:text-5xl text-white leading-[1.1] mb-5 tracking-tight">
              Tomorrow morning,<br />know exactly what to do.
            </motion.h2>
            <motion.p variants={fadeUp} className="font-sans text-white/60 text-base mb-8 leading-relaxed">
              Connect your POS today. Read your first brief in minutes.
            </motion.p>
            <motion.button variants={fadeUp} onClick={() => navigate('/auth')} className="inline-flex items-center gap-2 bg-white text-[#4A2D5E] hover:bg-[#FAF9F7] rounded-[8px] font-sans font-semibold px-8 py-4 text-sm transition-all shadow-lg hover:shadow-xl hover:-translate-y-px">
              Get started
              <ArrowRight size={15} strokeWidth={2} />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-[#E6E2DE] bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-6 sm:py-8 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF9F7]"><ClearPathLogo size="sm" /></Link>
            <span className="text-xs font-sans text-[#6B6670]">&copy; {new Date().getFullYear()} Clear Path SMB LLC</span>
          </div>
          <nav aria-label="Footer" className="flex flex-wrap items-center justify-center gap-x-5 gap-y-0">
            <Link to="/" className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-xs font-sans text-[#6B6670] hover:text-[#2D2A2E] transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF9F7]">Overview</Link>
            <a href="mailto:support@clearpathsmb.com" className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-xs font-sans text-[#6B6670] hover:text-[#2D2A2E] transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF9F7]">support@clearpathsmb.com</a>
            <Link to="/privacy" className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-xs font-sans text-[#6B6670] hover:text-[#2D2A2E] transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF9F7]">Privacy</Link>
            <Link to="/terms" className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-xs font-sans text-[#6B6670] hover:text-[#2D2A2E] transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF9F7]">Terms</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
